import React, { useCallback, useContext, useEffect, useState } from "react";

import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	List,
	ListItem,
	ListItemSecondaryAction,
	ListItemText,
	TextField,
	Typography,
} from "@material-ui/core";
import { DeleteOutline } from "@material-ui/icons";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

const TicketNotesDialog = ({ open, onClose, ticketId }) => {
	const { user } = useContext(AuthContext);
	const [notes, setNotes] = useState([]);
	const [text, setText] = useState("");
	const [saving, setSaving] = useState(false);

	const load = useCallback(async () => {
		try {
			const { data } = await api.get(`/tickets/${ticketId}/notes`);
			setNotes(data.notes || []);
		} catch (err) {
			toastError(err);
		}
	}, [ticketId]);

	useEffect(() => {
		if (open) {
			setText("");
			void load();
		}
	}, [open, load]);

	const add = async () => {
		if (!text.trim()) return;
		setSaving(true);
		try {
			await api.post(`/tickets/${ticketId}/notes`, { body: text });
			setText("");
			await load();
		} catch (err) {
			toastError(err);
		}
		setSaving(false);
	};

	const remove = async note => {
		try {
			await api.delete(`/tickets/${ticketId}/notes/${note.id}`);
			await load();
		} catch (err) {
			toastError(err);
		}
	};

	const canRemove = note => user?.profile === "admin" || Number(note.userId) === Number(user?.id);

	return (
		<Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth scroll="paper">
			<DialogTitle>{i18n.t("quality.notes")}</DialogTitle>
			<DialogContent dividers>
				<Typography variant="caption" color="textSecondary">
					{i18n.t("quality.notesHint")}
				</Typography>
				{notes.length === 0 ? (
					<Typography variant="body2" style={{ margin: "12px 0" }}>
						{i18n.t("quality.notesEmpty")}
					</Typography>
				) : (
					<List dense>
						{notes.map(note => (
							<ListItem key={note.id} alignItems="flex-start" disableGutters>
								<ListItemText
									primary={note.body}
									primaryTypographyProps={{ style: { whiteSpace: "pre-wrap" } }}
									secondary={`${note.userName || "—"} · ${new Date(note.createdAt).toLocaleString()}`}
								/>
								{canRemove(note) && (
									<ListItemSecondaryAction>
										<IconButton size="small" onClick={() => remove(note)}>
											<DeleteOutline fontSize="small" />
										</IconButton>
									</ListItemSecondaryAction>
								)}
							</ListItem>
						))}
					</List>
				)}
				<TextField
					fullWidth
					multiline
					rows={3}
					variant="outlined"
					margin="dense"
					placeholder={i18n.t("quality.notesPlaceholder")}
					value={text}
					onChange={e => setText(e.target.value)}
					inputProps={{ maxLength: 2000 }}
				/>
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose} variant="outlined" color="secondary">
					{i18n.t("quality.notesClose")}
				</Button>
				<Button onClick={add} disabled={saving || !text.trim()} variant="contained" color="primary">
					{i18n.t("quality.notesAdd")}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default TicketNotesDialog;
